import React, { useState } from 'react';

import { Image, Text } from '@change/ds-core-ui';
import { FacebookRegularIcon as FacebookIcon, RedditIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { RecommendedGroup } from '../../types';

type Props = {
	recommendedGroup: RecommendedGroup;
	platform: 'facebook' | 'reddit';
};

function FacebookIconImage() {
	return (
		<div className="flex h-full items-center justify-center bg-[#D1E4FC]" data-testid="facebook-default-icon">
			<div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-[#0866FF]">
				<FacebookIcon className="text-white" size={24} />
			</div>
		</div>
	);
}

function RedditIconImage() {
	return (
		<div className="flex h-full items-center justify-center bg-[#FFC7B2]" data-testid="reddit-default-icon">
			<div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-[#FF4500]">
				<RedditIcon className="text-white" size={24} />
			</div>
		</div>
	);
}

function FacebookImageContainer({ imageUrl, onImageError }: { imageUrl: string; onImageError: () => void }) {
	return (
		<div className="flex items-center justify-center overflow-hidden">
			<Image
				src={imageUrl}
				alt=""
				className="h-full w-full object-cover"
				data-testid="facebook-card-image"
				onError={onImageError}
			/>
		</div>
	);
}

function RedditImageContainer({ imageUrl, onImageError }: { imageUrl: string; onImageError: () => void }) {
	return (
		<div className="relative flex h-full w-full items-center justify-center overflow-hidden">
			<div className="absolute inset-0 flex h-full w-full items-center justify-center">
				<Image src={imageUrl} alt="" className="z-0 h-128 w-lg object-cover blur-[32px]" />
			</div>
			<div className="relative z-5 flex h-full w-full items-center justify-center">
				<Image
					src={imageUrl}
					alt=""
					className="h-12 w-12 rounded-full border-2 border-solid border-white object-contain"
					data-testid="reddit-card-image"
					onError={onImageError}
				/>
			</div>
		</div>
	);
}

const PLATFORM_UI: Record<
	'facebook' | 'reddit',
	{
		ImageContainer: React.FC<{ imageUrl: string; onImageError: () => void }>;
		DefaultImageContainer: React.FC;
	}
> = {
	facebook: {
		ImageContainer: FacebookImageContainer,
		DefaultImageContainer: FacebookIconImage,
	},
	reddit: {
		ImageContainer: RedditImageContainer,
		DefaultImageContainer: RedditIconImage,
	},
} as const;

export function RecommendedGroupCard(props: Props) {
	const { localizeNumber } = useI18n();
	const [imageLoadError, setImageLoadError] = useState(false);
	const {
		recommendedGroup: {
			name,
			metadata: { imageUrl, memberCount },
		},
		platform,
	} = props;

	const titleLineClamp = `line-clamp-${platform === 'facebook' ? 2 : 1}`;

	const { ImageContainer, DefaultImageContainer } = PLATFORM_UI[platform];

	return (
		<div className="flex h-full w-full max-w-full flex-row items-center overflow-hidden pr-10">
			<div className="h-[70px] w-[125px] shrink-0">
				{imageUrl && !imageLoadError ? (
					<ImageContainer
						imageUrl={imageUrl}
						onImageError={() => {
							setImageLoadError(true);
						}}
					/>
				) : (
					<DefaultImageContainer />
				)}
			</div>
			<div className="flex min-w-0 flex-1 flex-col justify-center p-4 pr-0">
				<Text data-testid="group-name-text" className={cn('text-size-body-bold', titleLineClamp)}>
					{name}
				</Text>
				{platform === 'reddit' && memberCount ? (
					<Text data-testid="member-count-text" className="text-size-body-small text-typography-weak">
						{localizeNumber(memberCount)} followers
					</Text>
				) : null}
			</div>
		</div>
	);
}
